import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, UserPlus, Send } from 'lucide-react';

interface StructuredFormField {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'email' | 'number' | 'checkbox' | 'tel';
  placeholder?: string;
  required: boolean;
  default_value?: string | boolean | null;
  defaultValue?: string | boolean | null; // Backend format
  options?: Array<{ value: string; label: string }>;
  validation?: {
    min_length?: number;
    max_length?: number;
    min?: number;
    max?: number;
  };
  help_text?: string | null;
  helperText?: string | null; // Backend format
}

interface StructuredFormData {
  form_id: string;
  title: string;
  description: string;
  fields: StructuredFormField[];
  submit_label: string;
  cancel_label?: string;
  prefilled_data?: Record<string, any>;
  callback?: {
    endpoint: string;
    method: string;
    action: string;
  };
}

// Backend format for form_request
interface BackendFormData {
  form_fields: Array<{
    name: string;
    label: string;
    type: string;
    required: boolean;
    placeholder?: string;
  }>;
  entity_type: string;
  form_title: string;
}

interface StructuredFormMessageProps {
  content: string;
  structured_data: {
    data: StructuredFormData | BackendFormData;
    response_type?: string;
  } | null;
  onSubmit?: (formData: Record<string, string>) => void;
}

/**
 * Component for rendering structured forms from AI responses
 */
export const StructuredFormMessage: React.FC<StructuredFormMessageProps> = ({ 
  content, 
  structured_data, 
  onSubmit 
}) => {
  const [formData, setFormData] = useState<Record<string, string | boolean | string[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Handle potential double nesting of structured_data and null checks
  const actualStructuredData = structured_data?.structured_data || structured_data;
  const rawFormConfig = actualStructuredData?.data;

  // Normalize form config to handle both backend and frontend formats
  const formConfig = rawFormConfig ? {
    form_id: (rawFormConfig as any).form_id || `form-${Date.now()}`,
    title: (rawFormConfig as any).title || (rawFormConfig as any).form_title || 'Form',
    description: (rawFormConfig as any).description || '',
    fields: ((rawFormConfig as any).fields || (rawFormConfig as any).form_fields || []).map((field: any) => ({
      ...field,
      // Normalize field properties to handle backend format
      default_value: field.default_value !== undefined ? field.default_value : field.defaultValue,
      help_text: field.help_text || field.helperText,
      required: field.required !== undefined ? field.required : false
    })),
    submit_label: (rawFormConfig as any).submit_label || 'Submit',
    cancel_label: (rawFormConfig as any).cancel_label || 'Cancel',
    entity_type: (rawFormConfig as any).entity_type || 'unknown'
  } : null;

  // Debug logging
  console.log('🔧 [StructuredFormMessage] COMPONENT CALLED with:', {
    content: content.substring(0, 50) + '...',
    has_structured_data: !!structured_data,
    structured_data,
    actualStructuredData,
    rawFormConfig,
    normalized_form_config: formConfig,
    fields_count: formConfig?.fields?.length || 0,
    fields_detail: formConfig?.fields?.map(f => ({
      name: f.name,
      type: f.type,
      required: f.required,
      default_value: f.default_value,
      defaultValue: f.defaultValue,
      has_options: !!f.options?.length
    }))
  });

  // Safety check
  if (!formConfig || !formConfig.fields || formConfig.fields.length === 0) {
    console.error('❌ [StructuredFormMessage] Invalid form config:', structured_data);
    return (
      <Card className="p-4 bg-red-50 border-red-200">
        <div className="text-red-800 text-sm">
          <AlertCircle className="h-4 w-4 inline mr-2" />
          Form configuration error. Please try again.
        </div>
      </Card>
    );
  }

  // Initialize form data with default values - only once when form mounts
  useEffect(() => {
    if (!formConfig?.fields) {
      console.log('🔧 [StructuredFormMessage] No form fields available for initialization');
      return;
    }

    console.log('🔧 [StructuredFormMessage] useEffect triggered for form initialization:', {
      formId: formConfig.form_id,
      fieldsCount: formConfig.fields.length,
      currentFormData: formData,
      hasFormData: Object.keys(formData).length > 0
    });

    // Don't reinitialize if we already have form data
    if (Object.keys(formData).length > 0) {
      console.log('🔧 [StructuredFormMessage] Form data already exists, skipping initialization');
      return;
    }
    
    const initialData: Record<string, string | boolean | string[]> = {};
    
    // Set default values from fields (handle both backend and frontend formats)
    formConfig.fields.forEach(field => {
      const defaultValue = field.default_value !== null && field.default_value !== undefined 
        ? field.default_value 
        : field.defaultValue;
        
      console.log(`🔧 [StructuredFormMessage] Processing field ${field.name}:`, {
        type: field.type,
        required: field.required,
        default_value: field.default_value,
        defaultValue: field.defaultValue,
        resolved_defaultValue: defaultValue,
        has_options: !!field.options?.length,
        options_count: field.options?.length || 0
      });
        
      if (defaultValue !== null && defaultValue !== undefined) {
        initialData[field.name] = defaultValue;
        console.log(`✅ [StructuredFormMessage] Set default value for ${field.name}:`, defaultValue);
      } else if (field.type === 'checkbox') {
        initialData[field.name] = false;
        console.log(`📋 [StructuredFormMessage] Set checkbox ${field.name} to false`);
      } else if (field.type === 'multiselect') {
        initialData[field.name] = [];
        console.log(`📋 [StructuredFormMessage] Set multiselect ${field.name} to empty array`);
      } else {
        initialData[field.name] = '';
        console.log(`📋 [StructuredFormMessage] Set ${field.name} to empty string`);
      }
    });
    
    console.log('🔧 [StructuredFormMessage] Final initial form data:', initialData);
    setFormData(initialData);
  }, [formConfig?.form_id]);

  // Debug form data changes
  useEffect(() => {
    console.log('🔧 [StructuredFormMessage] Form data state changed:', {
      formData,
      keys: Object.keys(formData),
      values: Object.values(formData)
    });
  }, [formData]);

  const handleFieldChange = (fieldName: string, value: string | boolean | string[]) => {
    console.log('📝 [StructuredFormMessage] Field change:', {
      fieldName,
      value,
      valueType: typeof value,
      currentFormData: formData
    });
    
    setFormData(prev => {
      const newData = { ...prev, [fieldName]: value };
      console.log('📝 [StructuredFormMessage] Updated form data:', newData);
      return newData;
    });
    
    // Clear error when field is changed
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };




  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit(formData);
    }
  };

  const renderSimpleField = (field: StructuredFormField) => {
    const value = formData[field.name];

    if (field.type === 'checkbox') {
      return (
        <div key={field.name} className="space-y-2">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleFieldChange(field.name, e.target.checked)}
              className="rounded"
            />
            <span className="text-sm font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </span>
          </label>
          {(field.help_text || field.helperText) && (
            <p className="text-xs text-gray-500 ml-6">{field.help_text || field.helperText}</p>
          )}
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.name} className="space-y-2">
          <Label htmlFor={field.name} className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Select
            value={value?.toString() || ''}
            onValueChange={(newValue) => handleFieldChange(field.name, newValue)}
            required={field.required}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={
                field.placeholder || 
                field.options?.find(option => option.value === "")?.label ||
                'Select...'
              } />
            </SelectTrigger>
            <SelectContent>
              {field.options?.filter(option => option.value !== "").map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(field.help_text || field.helperText) && (
            <p className="text-xs text-gray-500">{field.help_text || field.helperText}</p>
          )}
          {errors[field.name] && (
            <p className="text-xs text-red-500">{errors[field.name]}</p>
          )}
        </div>
      );
    }

    if (field.type === 'multiselect') {
      const selectedValues = Array.isArray(value) ? value : [];
      
      return (
        <div key={field.name} className="space-y-2">
          <label className="block text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-300 rounded p-2">
            {field.options?.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <label key={option.value} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      const newSelection = e.target.checked
                        ? [...selectedValues, option.value]
                        : selectedValues.filter(v => v !== option.value);
                      handleFieldChange(field.name, newSelection);
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              );
            })}
          </div>
          {(field.help_text || field.helperText) && (
            <p className="text-xs text-gray-500">{field.help_text || field.helperText}</p>
          )}
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div key={field.name} className="md:col-span-2 space-y-2">
          <Label htmlFor={field.name} className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Textarea
            id={field.name}
            value={value?.toString() || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            maxLength={field.validation?.max_length}
            className="min-h-20"
          />
          {(field.help_text || field.helperText) && (
            <p className="text-xs text-gray-500">{field.help_text || field.helperText}</p>
          )}
          {errors[field.name] && (
            <p className="text-xs text-red-500">{errors[field.name]}</p>
          )}
        </div>
      );
    }

    // Default to text input - using shadcn/ui components
    const inputType = field.type === 'tel' ? 'text' : field.type; // Map tel to text for better compatibility
    const isFullWidth = field.type === 'textarea' || field.name === 'email'; // Some fields should span full width
    
    return (
      <div key={field.name} className={`space-y-2 ${isFullWidth ? 'md:col-span-2' : ''}`}>
        <Label htmlFor={field.name} className="text-sm font-medium">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        <Input
          id={field.name}
          type={inputType}
          value={formData[field.name]?.toString() || ''}
          onChange={(e) => {
            console.log('🔄 Input onChange triggered:', field.name, e.target.value);
            handleFieldChange(field.name, e.target.value);
          }}
          placeholder={field.placeholder}
          required={field.required}
          maxLength={field.validation?.max_length}
          className="w-full"
        />
        {field.help_text && (
          <p className="text-xs text-gray-500">{field.help_text}</p>
        )}
        {errors[field.name] && (
          <p className="text-xs text-red-500">{errors[field.name]}</p>
        )}
      </div>
    );
  };

  console.log('🔧 [StructuredFormMessage] About to render form:', {
    formData,
    formDataKeys: Object.keys(formData),
    fieldsCount: formConfig?.fields?.length,
    fields: formConfig?.fields?.map(f => ({ name: f.name, type: f.type, required: f.required }))
  });
  
  // Get appropriate icon for form type
  const getFormIcon = () => {
    if (formConfig.entity_type === 'customer') {
      return <UserPlus className="h-5 w-5 text-blue-600" />;
    }
    return <Send className="h-5 w-5 text-gray-600" />;
  };

  return (
    <Card className="w-full bg-gradient-to-br from-slate-50 to-gray-50 border-slate-200 max-w-2xl">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          {getFormIcon()}
          {formConfig.title}
        </CardTitle>
        {formConfig.description && (
          <p className="text-sm text-gray-600 mt-1">{formConfig.description}</p>
        )}
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Form Fields - Responsive Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {formConfig.fields.map(renderSimpleField)}
        </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {formConfig.submit_label || 'Submit'}
                </>
              )}
            </Button>
            {formConfig.cancel_label && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setFormData({})}
              >
                {formConfig.cancel_label}
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};